import { MappingChange, TenantMapping } from "Pages/Mapping/Mapping";
import { apiRequest } from "PhishingManager/service/apiHelper";
import { ENVIRONMENT, Status } from "app-types";

let BASE_URL = process.env.REACT_APP_API_URL_PCI_COMPLIANCE as string;

if (!BASE_URL) {
    const urlhost = window.location.host;
    let env : ENVIRONMENT = "prod";
    if (urlhost.includes("ev.") || urlhost.includes("localhost")) env = "dev";
    if (urlhost.includes("tg.")) env = "stg";
    BASE_URL = `https://pci-compliance.${env}.venzagrc.com/api/pci-compliance`;
}

const baseUrl = BASE_URL;

export const getProvidersForTenant = async (
  tenantId: string,
  tenantName: string
) => {
  const url = `${baseUrl}/tenants/providers?tenant_id=${tenantId}&tenant_name=${tenantName}`;
  try {
    const response = await apiRequest(url);
    if (response.status !== Status.Success) {
      throw new Error(`API call failed: ${response.status}`);
    }
    return response;
  } catch (error) {
    console.error("Error fetching provider list:", error);
    throw error;
  }
};

export const updateMappingStatus = async (
  tenantId: number,
  providerId: number,
  status: 'enable' | 'disable'
) => {
  const url = `${baseUrl}/tenant-provider-mappings/update-status?tenant_id=${tenantId}&provider_id=${providerId}&status=${status}`;

  try {
    const response = await apiRequest(url, { method: 'POST' });
    if (response.status !== Status.Success) {
      throw new Error(`API call failed with status: ${response.status}`);
    }
    return response;
  } catch (error) {
    console.error("Error updating mapping status:", error);
    throw error;
  }
};

interface TenantInfo {
  id: string | number;
  name: string;
}

export interface PropertyMerchantMapping {
  property_id?: string | null;
  property_name: string | null;
  mid?: string | null;
}

interface ConfirmMappingRequest {
  provider_id: number | null;
  tenant: TenantInfo;
  property_merchant_mappings: PropertyMerchantMapping[];
}

export const confirmMapping = async (
  tenantInfo: TenantMapping,
  propertyMerchantMappings: PropertyMerchantMapping[]
) => {
  const url = `${baseUrl}/property-merchant-mappings`;

  const payload: ConfirmMappingRequest = {
    provider_id: tenantInfo.providerId,
    tenant: {
      id: tenantInfo.id,
      name: tenantInfo.name,
    },
    property_merchant_mappings: propertyMerchantMappings,
  };

  try {
    const response = await apiRequest(url, {
      method: "POST",
      body: JSON.stringify(payload),
    });

    if (response.status !== Status.Success) {
      throw {
        status: response.status,
        data: {},
      };
    }

    return response;
  } catch (error: any) {
    console.error("Error confirming mapping:", error);
    throw {
      status: error?.statusCode || 500,
      data: { message: error?.errors?.[0] ?? "Unknown error occurred", errors: error?.errors || [] }
    };
  }

};


export interface PciPortalProperty {
  mid: string;
  name: string;
  property_id: string | null;
  mapping_status?: string,
  count?: number;
}

export const getExternalMerchants = async (
  tenantId: number,
  providerId: number,
  merchantName?: string,
  mid?: string,
  visibleCount?: number,
  status?: string,
): Promise<PciPortalProperty[]> => {
  const url = !!visibleCount ? `${baseUrl}/external-merchants?tenant_id=${tenantId}&provider_id=${providerId}&merchant_name=${merchantName}&status=unmapped&page=${visibleCount}&per_page=10` :
    status === "mapped" ? `${baseUrl}/external-merchants?tenant_id=${tenantId}&provider_id=${providerId}&merchant_name=${merchantName}&status=mapped` : mid == null
      ? `${baseUrl}/external-merchants?tenant_id=${tenantId}&provider_id=${providerId}&merchant_name=${merchantName}&status=unmapped`
      : `${baseUrl}/external-merchants?tenant_id=${tenantId}&provider_id=${providerId}&merchant_name=${merchantName}&mids=${mid}`;

  try {
    const response = await apiRequest(url);
    if (response.status !== Status.Success) {
      throw new Error(`API call failed with status: ${response.status}`);
    }
    const responseData = response;
    if (!!visibleCount) {
      return responseData.data.map(
        (item: {
          merchant_name: string;
          external_mid: string;
          mapping_status?: string | null;
          property_id: string | null;
        }) => ({
          name: item.merchant_name,
          mid: item.external_mid,
          mapping_status: item?.mapping_status ?? "mapped",
          property_id: item.property_id,
          count: responseData.count, 
        })
      );
    }

    return responseData.data.map((item: { merchant_name: string; external_mid: string; mapping_status?: string | null; property_id: string | null; }) => ({
      name: item.merchant_name,
      mid: item.external_mid,
      mapping_status: item?.mapping_status ?? "mapped",
      property_id: item.property_id,
    }));
  } catch (error) {
    console.error("Error fetching external merchants:", error);
    throw error;
  }
};

export const getExistingMappedMerchants = async (
  tenantId: number,
  providerId: number,
  mappingStatus?: string,
): Promise<MappingChange[]> => {
  let mappingStatusParam = 'active';
  if (mappingStatus === "unmapped") {
    mappingStatusParam = 'unmapped';
  }
  const url = `${baseUrl}/property-merchant-mappings?tenant_id=${tenantId}&provider_id=${providerId}&status=${mappingStatusParam}`;

  try {
    const response = await apiRequest(url);
    if (response.status !== Status.Success) {
      throw new Error(`API call failed with status: ${response.status}`);
    }
    const responseData = response;

    const mappedData = responseData.data.property_merchant_mapping.map((item: any) => ({
      id: String(item.property_id),
      venzaProperty: item.property_name,
      pciPortalProperty: item.external_merchant_name,
      venzaPropertyId: String(item.property_id),
      pciPortalPropertyId: item.external_mid,
      mid: item.external_mid,
      status: item.status,
      changeType: "unchanged",
      action: null
    }));

    return [...mappedData, {
      id: "add-row",
      venzaProperty: "",
      venzaPropertyId: null,
      pciPortalPropertyId: null,
      pciPortalProperty: "",
      status: "active", // Default status for new row
      changeType: "unchanged",
      action: "",
      isAddRow: true
    }];

  } catch (error) {
    console.error("Error fetching external merchants:", error);
    throw error;
  }
};